import * as Toast from "@radix-ui/react-toast";
import classNames from "classnames";
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import usePrefersReducedMotion from "../../../shared/hooks/usePrefersReducedMotion";
import KuikaToast from "./kuika-toast";
import { ToastAction, ToastId, toastService } from "./toast-service";
import {
  applyToastAction,
  computeToastStack,
  IToastItem,
  IToastStack,
  IToastStackLayout,
  openToastIdsNewestFirst,
  TOAST_VISIBLE_LIMIT
} from "./toast-stack";
import "./kuika-toast.scss";

interface IKuikaToastProviderProps {
  children: React.ReactNode;
}

interface IToastTimer {
  /** Milliseconds still to run. */
  remaining: number;
  /** `Date.now()` when the running stretch started. */
  startedAt: number;
  /** The running `setTimeout`, or null while paused or waiting for a visible slot. */
  handle: number | null;
}

interface IToastStackItemStyle extends React.CSSProperties {
  "--toast-index"?: number;
  "--toast-peek-steps"?: number;
  "--toast-offset"?: string;
  "--toast-height"?: string;
}

interface IToastViewportStyle extends React.CSSProperties {
  "--toast-stack-content"?: string;
  "--toast-stack-gaps"?: number;
}

interface IToastStackItemProps {
  item: IToastItem;
  /** Where the stack puts this toast; unset once it has closed. */
  layout: IToastStackLayout | undefined;
  onMeasure: (id: ToastId, height: number) => void;
  onDismiss: (id: ToastId) => void;
  onExited: (id: ToastId) => void;
}

const HIDDEN_LAYOUT: IToastStackLayout = { index: TOAST_VISIBLE_LIMIT, isHidden: true, peekSteps: 0, offset: 0 };

const px = (value: number | undefined): string | undefined => (value === undefined ? undefined : `${value}px`);

const isSameLayout = (left: IToastStackLayout, right: IToastStackLayout): boolean =>
  left.index === right.index &&
  left.isHidden === right.isHidden &&
  left.peekSteps === right.peekSteps &&
  left.offset === right.offset &&
  left.height === right.height;

/**
 * One toast in the stack. The Radix root (`li`) owns enter/exit and swipe; the inner item owns the
 * stack position, so a swipe's `transform` and the stack's `translate`/`scale` never fight.
 */
const ToastStackItem = ({
  item,
  layout: stackLayout,
  onMeasure,
  onDismiss,
  onExited
}: IToastStackItemProps): JSX.Element => {
  const itemRef: React.RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
  const { id, type, title, message, action } = item;

  // A closing toast has left the stack but still plays its exit where it last stood. The last open
  // place is kept as state derived during render (React's documented pattern).
  const [lastOpenLayout, setLastOpenLayout] = useState<IToastStackLayout>(stackLayout ?? HIDDEN_LAYOUT);
  if (stackLayout !== undefined && !isSameLayout(stackLayout, lastOpenLayout)) {
    setLastOpenLayout(stackLayout);
  }
  const layout: IToastStackLayout = stackLayout ?? lastOpenLayout;

  // The card's natural height, read with the stack's forced height lifted for one synchronous
  // layout. Only content changes can change it (the card has a fixed width). offsetHeight, not the
  // bounding rect: the rect includes the enter animation's and the stack's `scale`.
  useLayoutEffect((): void => {
    const card: Element | null | undefined = itemRef.current?.firstElementChild;
    if (!(card instanceof HTMLElement)) return;
    const forcedHeight: string = card.style.height;
    card.style.height = "auto";
    const height: number = card.offsetHeight;
    card.style.height = forcedHeight;
    onMeasure(id, height);
  }, [id, type, title, message, action, onMeasure]);

  // A toast waiting for a slot is invisible, so nothing inside it may take focus or be read:
  // `inert` covers its close and copy buttons, which the root's tabIndex and aria-hidden do not.
  useLayoutEffect((): void => {
    if (itemRef.current !== null) itemRef.current.inert = layout.isHidden;
  }, [layout.isHidden]);

  const style: IToastStackItemStyle = {
    "--toast-index": layout.index,
    "--toast-peek-steps": layout.peekSteps,
    "--toast-offset": `${layout.offset}px`,
    "--toast-height": px(layout.height)
  };

  const handleOpenChange = (open: boolean): void => {
    if (!open) onDismiss(id);
  };

  const handleAnimationEnd = (event: React.AnimationEvent<HTMLLIElement>): void => {
    if (event.target === event.currentTarget && !item.open) onExited(id);
  };

  return (
    <Toast.Root
      open={item.open}
      onOpenChange={handleOpenChange}
      duration={Infinity}
      className={classNames("kuika-toast-root", { "kuika-toast-root--hidden": layout.isHidden })}
      aria-hidden={layout.isHidden ? true : undefined}
      tabIndex={layout.isHidden ? -1 : 0}
      onAnimationEnd={handleAnimationEnd}
    >
      <div
        ref={itemRef}
        className={classNames("kuika-toast-stack__item", {
          "kuika-toast-stack__item--front": layout.index === 0,
          "kuika-toast-stack__item--hidden": layout.isHidden
        })}
        style={style}
      >
        <KuikaToast type={type} title={title} message={message} action={action} onClose={(): void => onDismiss(id)} />
      </div>
    </Toast.Root>
  );
};

/**
 * Mounts the toast viewport and renders every toast `toastService` sends.
 *
 * - At most `TOAST_VISIBLE_LIMIT` toasts show, newest in front with older ones peeking behind; the
 *   stack opens into a column while the pointer or focus is on it. Older toasts stay mounted but
 *   invisible, and their time only starts once a slot frees up.
 * - The provider runs each toast's timer (Radix's is off: it cannot restart when a loading toast
 *   settles). Time pauses while the stack is hovered or focused and while the window is blurred.
 * - A closing toast plays its exit before it leaves the DOM.
 */
export const KuikaToastProvider = ({ children }: IKuikaToastProviderProps): JSX.Element => {
  const [toasts, setToasts] = useState<IToastItem[]>([]);
  const [heights, setHeights] = useState<Record<ToastId, number>>({});
  const [isPointerInside, setIsPointerInside] = useState<boolean>(false);
  const [isFocusInside, setIsFocusInside] = useState<boolean>(false);
  const [isWindowBlurred, setIsWindowBlurred] = useState<boolean>(false);
  const prefersReducedMotion: boolean = usePrefersReducedMotion();

  const timersRef: React.MutableRefObject<Map<ToastId, IToastTimer>> = useRef<Map<ToastId, IToastTimer>>(new Map());
  const viewportRef: React.RefObject<HTMLOListElement> = useRef<HTMLOListElement>(null);

  const clearTimer = useCallback((id: ToastId): void => {
    const timer: IToastTimer | undefined = timersRef.current.get(id);
    if (timer?.handle !== null && timer?.handle !== undefined) {
      window.clearTimeout(timer.handle);
    }
    timersRef.current.delete(id);
  }, []);

  const dismiss = useCallback(
    (id: ToastId): void => {
      clearTimer(id);
      setToasts((previous: IToastItem[]): IToastItem[] =>
        applyToastAction(previous, { kind: "dismiss", id }, Date.now())
      );
    },
    [clearTimer]
  );

  useEffect(
    (): (() => void) =>
      toastService.subscribe((action: ToastAction): void => {
        // A new toast or a changed one starts a fresh timer with its (new) duration.
        if (action.kind === "show") clearTimer(action.toast.id);
        if (action.kind === "update") clearTimer(action.id);
        setToasts((previous: IToastItem[]): IToastItem[] => applyToastAction(previous, action, Date.now()));
      }),
    [clearTimer]
  );

  // Hover and focus are read from the DOM, not from React props: each toast is portalled into the
  // viewport, so the viewport is its DOM ancestor but not its React one, and React's synthetic
  // enter/focus events (which follow the React tree) never reach the viewport from a toast.
  useEffect((): (() => void) | undefined => {
    const viewport: HTMLOListElement | null = viewportRef.current;
    if (viewport === null) return undefined;
    const handlePointerEnter = (): void => setIsPointerInside(true);
    const handlePointerLeave = (): void => setIsPointerInside(false);
    const handleFocusIn = (): void => setIsFocusInside(true);
    const handleFocusOut = (event: FocusEvent): void => {
      const next: EventTarget | null = event.relatedTarget;
      if (!(next instanceof Node) || !viewport.contains(next)) setIsFocusInside(false);
    };
    viewport.addEventListener("pointerenter", handlePointerEnter);
    viewport.addEventListener("pointerleave", handlePointerLeave);
    viewport.addEventListener("focusin", handleFocusIn);
    viewport.addEventListener("focusout", handleFocusOut);
    return (): void => {
      viewport.removeEventListener("pointerenter", handlePointerEnter);
      viewport.removeEventListener("pointerleave", handlePointerLeave);
      viewport.removeEventListener("focusin", handleFocusIn);
      viewport.removeEventListener("focusout", handleFocusOut);
    };
  }, []);

  useEffect((): (() => void) => {
    const handleBlur = (): void => setIsWindowBlurred(true);
    const handleFocus = (): void => setIsWindowBlurred(false);
    window.addEventListener("blur", handleBlur);
    window.addEventListener("focus", handleFocus);
    return (): void => {
      window.removeEventListener("blur", handleBlur);
      window.removeEventListener("focus", handleFocus);
    };
  }, []);

  // Run the visible toasts' timers, pause the rest, forget the ones that are gone.
  useEffect((): void => {
    const timers: Map<ToastId, IToastTimer> = timersRef.current;
    const now: number = Date.now();
    const visible: Set<ToastId> = new Set<ToastId>(openToastIdsNewestFirst(toasts).slice(0, TOAST_VISIBLE_LIMIT));
    const isPaused: boolean = isPointerInside || isFocusInside || isWindowBlurred;
    const timed: Set<ToastId> = new Set<ToastId>();

    toasts.forEach((item: IToastItem): void => {
      if (!item.open || !Number.isFinite(item.duration)) return;
      timed.add(item.id);
      const timer: IToastTimer = timers.get(item.id) ?? { remaining: item.duration, startedAt: now, handle: null };
      timers.set(item.id, timer);
      const shouldRun: boolean = visible.has(item.id) && !isPaused;
      if (shouldRun && timer.handle === null) {
        timer.startedAt = now;
        timer.handle = window.setTimeout((): void => dismiss(item.id), timer.remaining);
      } else if (!shouldRun && timer.handle !== null) {
        window.clearTimeout(timer.handle);
        timer.remaining = Math.max(0, timer.remaining - (now - timer.startedAt));
        timer.handle = null;
      }
    });

    timers.forEach((_timer: IToastTimer, id: ToastId): void => {
      if (!timed.has(id)) clearTimer(id);
    });
  }, [toasts, isPointerInside, isFocusInside, isWindowBlurred, dismiss, clearTimer]);

  // A stack that empties under a still pointer gets no pointerleave; the next toast must not
  // inherit a hover that ended with the last one. Reset during render, not in an effect.
  const hasOpenToast: boolean = toasts.some((item: IToastItem): boolean => item.open);
  if (!hasOpenToast && isPointerInside) {
    setIsPointerInside(false);
  }

  useEffect(
    (): (() => void) => (): void => {
      timersRef.current.forEach((timer: IToastTimer): void => {
        if (timer.handle !== null) window.clearTimeout(timer.handle);
      });
      timersRef.current.clear();
    },
    []
  );

  const handleMeasure = useCallback((id: ToastId, height: number): void => {
    setHeights((previous: Record<ToastId, number>): Record<ToastId, number> =>
      previous[id] === height ? previous : { ...previous, [id]: height }
    );
  }, []);

  const handleExited = useCallback((id: ToastId): void => {
    setToasts((previous: IToastItem[]): IToastItem[] => previous.filter((item: IToastItem): boolean => item.id !== id));
    setHeights((previous: Record<ToastId, number>): Record<ToastId, number> => {
      if (!(id in previous)) return previous;
      const next: Record<ToastId, number> = { ...previous };
      delete next[id];
      return next;
    });
  }, []);

  const isExpanded: boolean = isPointerInside || isFocusInside;
  const stack: IToastStack = computeToastStack(openToastIdsNewestFirst(toasts), heights, isExpanded);

  const viewportStyle: IToastViewportStyle = {
    "--toast-stack-content": `${stack.contentHeight}px`,
    "--toast-stack-gaps": stack.gapCount
  };

  return (
    <Toast.Provider swipeDirection="right" duration={Infinity}>
      {children}
      {toasts.map((item: IToastItem): JSX.Element => (
        <ToastStackItem
          key={item.id}
          item={item}
          layout={stack.layouts.get(item.id)}
          onMeasure={handleMeasure}
          onDismiss={dismiss}
          onExited={handleExited}
        />
      ))}
      <Toast.Viewport
        ref={viewportRef}
        className={classNames("kuika-toast-viewport", {
          "kuika-toast-viewport--expanded": isExpanded,
          "kuika-toast-viewport--reduced-motion": prefersReducedMotion
        })}
        style={viewportStyle}
      />
    </Toast.Provider>
  );
};

export default KuikaToastProvider;
